import { useState, useRef, useCallback } from 'react'
import { useAuthStore } from '../store'
import { saveAuthData } from '../../api/secureStore'
import { pgInvoke } from '../../api/pgInvoke'
import { resolveAvatarUrl } from '../casdoor'

export interface UseAvatarUploadOptions {
  onSuccess?: (avatarUrl: string) => void
  onError?: (error: string) => void
  onProgress?: (progress: number) => void
}

export interface UseAvatarUploadReturn {
  isUploading: boolean
  progress: number
  fileInputRef: React.RefObject<HTMLInputElement>

  openFilePicker: () => void
  handleFileChange: (file: File | null) => Promise<void>
  resetUpload: () => void
}

// ★ 头像上传大小限制（统一来源）：10MB 覆盖绝大多数手机照片，裁剪后实际上传体积极小
const MAX_FILE_SIZE = 10 * 1024 * 1024
export const MAX_AVATAR_FILE_SIZE = MAX_FILE_SIZE
const ALLOWED_TYPES = [
  'image/jpeg',
  'image/jpg',
  'image/png',
  'image/webp',
]

const validateFile = (file: File): string | null => {
  if (!ALLOWED_TYPES.includes(file.type)) {
    return '请选择图片文件 (JPG, PNG, WebP)'
  }
  if (file.size > MAX_FILE_SIZE) {
    return '图片大小不能超过 10MB'
  }
  return null
}

export function useAvatarUpload(
  options: UseAvatarUploadOptions = {}
): UseAvatarUploadReturn {
  const [isUploading, setIsUploading] = useState(false)
  const [progress, setProgress] = useState(0)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const progressIntervalRef = useRef<NodeJS.Timeout | null>(null)

  const clearProgressInterval = useCallback(() => {
    if (progressIntervalRef.current) {
      clearInterval(progressIntervalRef.current)
      progressIntervalRef.current = null
    }
  }, [])

  const simulateProgress = useCallback(() => {
    clearProgressInterval()
    progressIntervalRef.current = setInterval(() => {
      setProgress((prev) => {
        if (prev >= 90) {
          clearProgressInterval()
          return 90
        }
        const increment = Math.max(5, (90 - prev) / 5)
        return Math.min(prev + increment, 90)
      })
    }, 200)
  }, [clearProgressInterval])

  const openFilePicker = useCallback(() => {
    if (!isUploading && fileInputRef.current) {
      fileInputRef.current.click()
    }
  }, [isUploading])

  const handleFileChange = useCallback(
    async (file: File | null) => {
      if (!file) return

      const validationError = validateFile(file)
      if (validationError) {
        options.onError?.(validationError)
        return
      }

      setIsUploading(true)
      setProgress(0)
      simulateProgress()

      try {
        const { invoke } = await import('@tauri-apps/api/core')
        const user = useAuthStore.getState().user
        const accessToken = useAuthStore.getState().accessToken

        if (!user?.name || !accessToken) {
          throw new Error('未登录或登录已过期，请重新登录')
        }

        const buffer = await file.arrayBuffer()
        const imageData = Array.from(new Uint8Array(buffer))

        const result = await invoke<{ avatar_url: string; success: boolean; message: string }>(
          'upload_avatar_to_casdoor',
          {
            imageData,
            filename: file.name,
            mimeType: file.type,
            userName: user.name,
            accessToken,
          }
        )

        clearProgressInterval()
        setProgress(100)

        if (!result.success) {
          throw new Error(result.message || '头像上传失败')
        }

        // 统一存完整 URL（与登录回调时 resolveAvatarUrl 处理一致）
        const fullAvatarUrl = resolveAvatarUrl(result.avatar_url)

        useAuthStore.getState().setUser({
          ...user,
          avatar: fullAvatarUrl,
        })

        const state = useAuthStore.getState()
        saveAuthData({
          accessToken: state.accessToken!,
          refreshToken: state.refreshToken!,
          expiresAt: state.expiresAt!,
          user: { ...user, avatar: fullAvatarUrl },
        }).catch(err => console.warn('[AvatarUpload] 持久化头像到 secureStore 失败:', err))

        try {
          await invoke('cache_avatar_locally', {
            avatarUrl: fullAvatarUrl,
            username: user.name,
            accessToken,
            // ★ forceRefresh：头像 URL 是固定路径（avatar/{username}.jpg），
            // 上传后 URL 不变但内容已变，必须强制重新下载，否则 URL 指纹命中旧缓存
            forceRefresh: true,
          })
        } catch (err) {
          console.warn('[AvatarUpload] 头像本地缓存失败:', err)
        }

        // ★ 跨设备头像更新通知：写入 user_avatars 表版本，
        // 其他设备通过 Electric Shape 实时收到变更 → 主动刷新本地头像缓存
        try {
          // ★ IDOR 防护：userId 由 Rust 侧从当前登录会话获取，前端不传
          await pgInvoke('pg_upsert_user_avatar', {
            avatarUrl: fullAvatarUrl,
          })
          console.log('[AvatarUpload] 头像版本已写入，其他设备将同步更新:', user.id)
        } catch (err) {
          console.warn('[AvatarUpload] 写入头像版本失败（不影响本设备显示）:', err)
        }

        useAuthStore.getState().incrementAvatarCacheKey()

        options.onSuccess?.(fullAvatarUrl)
      } catch (error) {
        clearProgressInterval()
        const errorMessage =
          error instanceof Error ? error.message : '头像上传失败'
        options.onError?.(errorMessage)
      } finally {
        setIsUploading(false)
        if (fileInputRef.current) {
          fileInputRef.current.value = ''
        }
      }
    },
    [options, simulateProgress, clearProgressInterval]
  )

  const resetUpload = useCallback(() => {
    clearProgressInterval()
    setIsUploading(false)
    setProgress(0)
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }, [clearProgressInterval])

  return {
    isUploading,
    progress,
    fileInputRef,
    openFilePicker,
    handleFileChange,
    resetUpload,
  }
}
